import React from 'react';
import { Home, Bath, Square, MapPin, Phone, Mail } from 'lucide-react';
import Modal from '../property/Modal';
import { QRCodeSVG } from 'qrcode.react';
import ProfileImage from '../property/ProfileImage';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';

// Componente de imagen con manejo de errores para propiedades
const PropertyImage = ({ url, alt, className }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
    />
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const RealEstateCard = ({ property }) => {
  // Asegurar que siempre tengamos un objeto vendedor válido
  const vendedor = property?.vendedor || {
    nombre: 'KAIROS INMOBILIARIA',
    foto_perfil: null,
    tipo_usuario_id: 1,
    email_verificado: false,
    telefono_verificado: false,
    ubicacion: 'PIURA, PERÚ',
    sobre_mi: '',
    sobre_nosotros: '',
    whatsapp: '',
    telefono: '',
    facebook: '',
    instagram: '',
    nombre_usuario: ''
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  // Determine URL based on property type - Corregido para usar operacion en lugar de tipo
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property.id}`;
  };

  const tipoUsuario = {
    1: 'Cliente',
    2: 'Vendedor',
    3: 'Constructora',
    4: 'Inmobiliaria'
  }[vendedor.tipo_usuario_id] || 'Usuario';

  const features = [
    { icon: <Home className="w-4 h-4" />, label: `${property.habitaciones} Habitaciones` },
    { icon: <Bath className="w-4 h-4" />, label: `${property.banos} Baños` },
    { icon: <Square className="w-4 h-4" />, label: `${property.area_total}m²` },
    { icon: <Home className="w-4 h-4" />, label: 'Cocina' },
    { icon: <Home className="w-4 h-4" />, label: 'Jardín' },
    { icon: <Home className="w-4 h-4" />, label: 'Estacionamiento' }
  ];

  return (
    <div className="max-w-2xl bg-gradient-to-b from-slate-800 to-slate-900 p-6 rounded-lg shadow-xl">
      {/* Header with Seller Info */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 border-2 border-yellow-400 rounded-full p-0.5">
            <ProfileImage 
              url={vendedor.foto_perfil}
              alt={vendedor.nombre || "KAIROS INMOBILIARIA"}
              className="w-full h-full object-cover rounded-full"
            />
          </div>
          <div className="text-white">
            <div className="font-bold text-lg">{vendedor.nombre || 'KAIROS INMOBILIARIA'}</div>
            <div className="text-xs text-gray-300 flex items-center">
              {tipoUsuario}
              {(vendedor.email_verificado && vendedor.telefono_verificado) && (
                <span className="text-green-400 ml-2 flex items-center">
                  <svg viewBox="0 0 24 24" className="w-3 h-3 mr-1 fill-current">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                  </svg>
                  Verificado{tipoUsuario === 'Constructora' || tipoUsuario === 'Inmobiliaria' ? '/a' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          <div className="bg-yellow-400 text-black px-3 py-1 text-xs rounded-full">
            {property.operacion === 'alquiler' ? 'Alquiler' : 'Venta'}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative">
        {/* Title and Location */}
        <div className="text-center mb-6">
          <div className="text-yellow-400 text-sm italic flex items-center justify-center gap-1">
            <MapPin size={14} />
            {property.ubicacion}
          </div>
          <div className="text-white text-3xl font-bold mt-1">
            {property.titulo}
          </div>
          <div className="text-gray-300 text-sm mt-2 max-w-lg mx-auto">
            {property.descripcion_corta || 'Descubre una propiedad única con características excepcionales.'}
          </div>
        </div>

        <div className="flex gap-4">
          {/* Main Image with Gallery */}
          <div className="flex-1 relative">
            <PropertyImage 
              url={property.imagenes[0]}
              alt="Main property view"
              className="w-full h-80 object-cover rounded-lg"
            />
            
            {/* Mini Gallery */}
            <div className="absolute bottom-4 left-4 flex gap-2">
              {property.imagenes.slice(1, 4).map((img, index) => (
                <div key={index} className="w-14 h-14 rounded-md overflow-hidden border-2 border-white shadow-lg">
                  <PropertyImage 
                    url={img}
                    alt={`Property view ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            
            {/* QR Code */}
            <div className="absolute top-4 right-4 bg-white p-2 rounded-lg shadow-lg">
              <QRCodeSVG value={getPropertyUrl()} size={80} />
            </div>
          </div>
        </div>

        {/* Price Tag */}
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 p-4 rounded-lg flex justify-between items-center mt-6 shadow-lg">
          <div>
            <div className="text-black text-sm font-medium">{property.operacion === 'alquiler' ? 'Precio de Alquiler' : 'Precio de Venta'}</div>
            <div className="text-2xl font-bold">S/ {formatPrice(property.precio_soles)}</div>
            <div className="text-sm text-gray-800">USD {formatPrice(property.precio_dolares)}</div>
          </div>
          
          {/* CTA Button */}
          <button className="bg-black text-yellow-400 px-6 py-3 rounded-lg font-medium hover:bg-gray-900 transition-colors duration-300">
            Agenda tu Cita
          </button>
        </div>

        {/* Features and Contact */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Features */}
          <div className="bg-slate-700 p-4 rounded-lg">
            <div className="text-yellow-400 font-bold mb-3 border-b border-slate-600 pb-2">CARACTERÍSTICAS</div>
            <div className="grid grid-cols-2 gap-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center text-white text-sm">
                  <span className="text-yellow-400 mr-2">{feature.icon}</span>
                  {feature.label}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-700 p-4 rounded-lg">
            <div className="text-yellow-400 font-bold mb-3 border-b border-slate-600 pb-2">CONTACTO</div>
            <div className="text-white space-y-3">
              <div className="flex items-center gap-3">
                <Phone className="text-yellow-400 w-5 h-5" />
                <div>
                  <div className="text-sm text-gray-300">Teléfono</div>
                  <div>{property.telefono || vendedor.telefono || '+51 123 456 789'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="text-yellow-400 w-5 h-5" />
                <div>
                  <div className="text-sm text-gray-300">Email</div>
                  <div>{property.email || vendedor.email || 'contacto@inkayros.com'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="text-yellow-400 w-5 h-5" />
                <div>
                  <div className="text-sm text-gray-300">Ubicación</div>
                  <div>{property.ubicacion}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Website URL */}
        <div className="mt-6 text-center text-white">
          <div className="text-sm text-gray-400">Más información en:</div>
          <div className="font-medium text-yellow-400">{getPropertyUrl()}</div>
        </div>
      </div>
    </div>
  );
};

// Preview component
export const RealEstateCardPreview = ({ isOpen, onClose, property }) => {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-4xl mx-auto">
        <RealEstateCard property={property} />
        <button
          onClick={onClose}
          className="mt-6 w-full bg-gradient-to-r from-blue-700 to-blue-900 
                   text-white py-4 px-6 rounded-xl font-semibold 
                   shadow-lg hover:shadow-xl transition-all duration-300 
                   hover:scale-105 active:scale-100"
        >
          Cerrar
        </button>
      </div>
    </Modal>
  );
};

export default RealEstateCard;